import React, { useState, useEffect } from "react";
import Button from "@components/Button";
import CustomInput, { CustomSelectInput } from "@components/CustomInput/CustomInput";
import { Home } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router";
import styles from "./ViewUserPage.module.css";
import HistoryTable from "@components/HistoryTable/HistoryTable";
import { Avatar } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from "@mui/material/TextField";
import { useSelector } from "react-redux";
import usePostPay from "@hooks/usePostPay";
import PostPayDoctorCard from "@components/PostPayDoctorCard/PostPayDoctorCard";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import generateBillPDF from "@components/DownloadPdf/DownloadPdf";

const ViewUserPage = () => {
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateCustom, setSelectedDateCustom] = useState(null);
  const [currentStep, setCurrentStep] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileURL, setFileURL] = useState(null);
  const [selectedMode, setMode] = useState(null);
  const [selectedAmount, setAmount] = useState(null);
  const [payStatus, setPayStatus] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - 1);
  const previousMonthDate = currentDate.toISOString();
  const { handleOutstandingAmount, handleCustomOutstandingPayment, handlePostPayBill, handlePostBillPayment } = usePostPay();
  const {postPaymentOutstanding, postPaymentOutstandingCustom, monthlyPostPayBill} = useSelector((state) => state.postpay);
  const location = useLocation();
  const {doctor} = location.state || {};

  useEffect(() => {
    if (doctor?._id) {
      handleOutstandingAmount(doctor._id);
    }
  }, []);

  useEffect(()=>{
    const doctorId = doctor._id;
    const paid = false;
    const data = {doctorId, date: previousMonthDate, paid};
    handleCustomOutstandingPayment(data);
  },[]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setFileURL(URL.createObjectURL(file));
    }
  };

  const handleBack = () => {
    if(currentStep === "upload"){
      setUploadedFile(null);
      setFileURL(null);
      setCurrentStep("search");
    }else if(currentStep === "next"){
      setAmount(null);
      setMode(null);
      setCurrentStep("upload");
    }else if(currentStep === "comment"){
      setComment(null);
      setCurrentStep("next");
    }else if(currentStep === "preview"){
      setCurrentStep("comment");
    }
  }

const handleNext = () => {
    setError(""); 
    if (currentStep === "upload") {
        if (!uploadedFile) {
            setError("Please upload the file!");
            return;
        }
        setCurrentStep("next");
    } 
    else if (currentStep === "next") {
        if (!selectedAmount || !selectedMode || !selectedDateCustom) {
            setError("Please select date, amount and mode of payment!");
            return;
        } 
        else if(checkMonthlyPostPayBill()===false)
          return;
        setCurrentStep("comment");
        setError("");
        return;
    } 
    else if (currentStep === "comment") {
        if (!comment) {
            setError("Please enter a comment!");
            return;
        }
        setCurrentStep("preview");
    }
};


  useEffect(() => {
    const fetchMonthlyData = () => {
      const paid = payStatus==="paid";
      const doctorId = doctor._id;
      const date = new Date(selectedDate?.format("YYYY-MM-DD")); 
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      
      handlePostPayBill({doctorId, date: date.toISOString(), paid});
    };
    if (doctor?._id, selectedDate, payStatus) {
      fetchMonthlyData();
    }
  }, [selectedDate, payStatus]);

  const total = () => {
    if (monthlyPostPayBill?.data?.length && Array.isArray(monthlyPostPayBill.data)) {
        return monthlyPostPayBill.data.reduce((sum, item) => {
            return sum + parseFloat(item.pay.$numberDecimal);
        }, 0);
    }
    return 0;
};

  const handleSearch = () => { 
    setError("");
    if (selectedDate != null && payStatus !== "") {
      setCurrentStep("search");
    }else {
      setError("Please select date and payment status!");
    }
  };

  const handlePay = () => {
    setError("");
    if(payStatus=='paid'){
      setError("Already Paid!");
    }
    else{
      setCurrentStep("upload");
    }
  }

  const handleNavigateDashboard = () => {
    navigate("/");
  };

  const handleNavigateUpgradeAccessPage = () => {
    navigate("/managepostpay/upgradeaccess");
  };

  const handleNavigateHistoryPage = () => {
    navigate("/managepostpay/postpayhistory", 
      {
       state:{
         doctor:doctor
       }
      });
  };

  const handleNavigateManagePostPayPage = () => {
    navigate("/managepostpay");
  };

  const handleBillPayment = () => {
    const formData = new FormData();
    formData.append("doctorId", doctor._id);
    formData.append("date", selectedDateCustom.toISOString());
    formData.append("amount", selectedAmount);
    formData.append("mode", selectedMode);
    formData.append("comment", comment);
    
    if (uploadedFile) {
      formData.append("proofUri", uploadedFile);
    }

    handlePostBillPayment(formData);
    return handleNavigateHistoryPage();
  }

  const paidOptions = [
    { option: "Paid", value: "paid" },
    { option: "Unpaid", value: "unpaid" },
  ];

  const paymentModeOptions = [
    { option: "UPI", value: "UPI"},
    { option: "Net Banking", value: "Net Banking"},
    { option: "Credit Card", value: "Credit Card"},
    { option: "Debit Card", value: "Debit Card"},
    { option: "Other", value: "Other"},
  ]

  const columns = [
    { id: "reciept", label: "Receipt" },
    { id: "patientName", label: "Patient Name" },
    { id: "chosenPackage", label: "Package" },
    { id: "amount", label: "Amount" },
    { id: "date", label: "Date" },
    { id: "payStatus", label: "Payment Status" },
  ];

  const createData = (reciept, patientName, chosenPackage, date, amount, payStatus) => {
    const formattedDate = new Date(date).toLocaleDateString("en-GB");
    console.log(reciept)
    return {
      reciept,
        patientName,
        chosenPackage,
        date: formattedDate,
        amount,
        payStatus
    };
  };

  const rows = Array.isArray(monthlyPostPayBill?.data)
    ? monthlyPostPayBill.data.map((payment) =>
        createData(
          payment.receipt,
          `${payment.firstName} ${payment.lastName}`,
          payment.package,
          new Date(payment.updated_at).toISOString().split("T")[0],
          parseFloat(payment.pay?.$numberDecimal) || 0,
          payment.paid ? "Paid" : "Unpaid"
        )
      )
    : [];

  const formatPeriod = (date) => {
    const options = { month: "long", year: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const checkMonthlyPostPayBill = () => {
    return monthlyPostPayBill.data.some(bill => {
        setError("");
        const paymentDate = formatPeriod(selectedDate);
        const due = total().toString();
        if(due !== selectedAmount){
          setError(`Enter the date and correct amount! Your due for  ${paymentDate} is ${due}`);
        }
        return due === selectedAmount;
    });
  };

  return (
    <>
      <div className={styles.MenuWrapper}>
        <div className={styles.NavigationWrapper}>
          <Home className={styles.HomeIcon} onClick={handleNavigateDashboard} />
          <div>
            <span className={styles.DashboardText} onClick={handleNavigateDashboard}>
              Dashboard
            </span>
            <span className={styles.DashboardText} onClick={handleNavigateManagePostPayPage}>
              /PostPay Panel
            </span>
            <span className={styles.RouteText}>/View User</span>
          </div>
        </div>
        <div className={styles.ActionItemsWrapper}>
          <Button
            className={styles.BtnClass}
            text={"View History"}
            btnType={"secondary"}
            onClick={handleNavigateHistoryPage}
          />
          <Button
            className={styles.BtnClass}
            text={"Upgrade Access"}
            btnType={"secondary"}
            onClick={handleNavigateUpgradeAccessPage}
          />
        </div>
      </div>
      <div className={styles.Content}>
        <PostPayDoctorCard 
          firstName={doctor.local.firstName} 
          lastName={doctor.local.lastName}
          gender={doctor.local.gender}
          contact={doctor.local.contact}
          email={doctor.local.email}
          address={doctor.local.address}
          institute={doctor.local.instituteName}
          pincode={doctor.local.pincode}
        />
        <div className={styles.divider}></div>
          <div className={styles.options}>
            <div className={styles.firstRow}>
              <Button
                className={styles.BtnClass}
                text={"Total Outstanding : " + `${postPaymentOutstanding?.outstandingAmount}`}
              />
              <Button
                className={styles.BtnClass}
                text={"Previous Month : " + `${postPaymentOutstandingCustom?.outstandingAmount}`}
              />
            </div>
            <div className={styles.lastRow}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  className={styles.dateInput}
                  value={selectedDate}
                  onChange={(value) => setSelectedDate(value)}
                  TextField={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>

              <CustomSelectInput
                className={styles.CustomSelect}
                selectOptions={paidOptions}
                defaultOption="Paid/Unpaid"
                value={payStatus}
                onChange={(event) => setPayStatus(event.target.value)}
                name="payStatus"
              />
              <Button
                className={styles.BtnClass}
                btnType={"primary"}
                text={"Search"}
                onClick={handleSearch}
              />
            </div>
            {currentStep === "search" && (
              <div className={styles.searchResults}>
                <div className={styles.infoBar}>
                  <div className={styles.selectedInfo}>
                    <p><strong>Period:</strong> {formatPeriod(selectedDate)}</p>
                    <p><strong>Amount:</strong> {total()}</p>
                    <p><strong>Status:</strong> {payStatus}</p>
                  </div>
                  <div className={styles.buttonContainer}>
                    <Button 
                    className={styles.BtnClass} 
                    text="Generate Bill"
                    onClick={() => generateBillPDF(doctor.local.firstName+" "+doctor.local.lastName, new Date(selectedDate).toLocaleString('default', { month: 'long' })+" "+new Date(selectedDate).getFullYear(), `${total()}`, payStatus, rows, columns)} 
                    />
                    <Button
                      className={styles.BtnClass}
                      text="Pay"
                      btnType={"primary"}
                      onClick={handlePay}
                    />
                  </div>
                </div>
                <div className={styles.Wrapper}>
                  {rows.length === 0 ? (
                    <p style={{ fontSize: "12px" }}>No records available</p>
                  ) : (
                    <HistoryTable
                      rows={rows}
                      columns={columns}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={setRowsPerPage}
                      page={page}
                      setPage={setPage}
                      totalRecords={monthlyPostPayBill?.total_record ?? 0}
                    />
                  )}
                </div>;
              </div>
            )}
            {currentStep === "upload" && (
              <>
                <div className={styles.selectedInfo}>
                  <p><strong>Period:</strong> {formatPeriod(selectedDate)}</p>
                  <p><strong>Amount:</strong> {total()}</p>
                  <p><strong>Status:</strong> {payStatus}</p>
                </div>
                <div className={styles.uploadContainer}>
                  <p>Upload Payment Proof:</p>
                  <input type="file" onChange={handleFileUpload}/>
                  {error && <p style={{ color: "red", fontSize: "14px", marginTop: "8px" }}>{error}</p>}
                  <div className={styles.buttonContainer}>
                    <Button className={styles.BtnClass} text="Back" onClick={handleBack} />
                    <Button className={styles.BtnClass} text="Next" onClick={handleNext} />
                  </div>
                </div>
              </>
            )}

            {currentStep === "next" && (
            <>
              <div className={styles.selectedInfo}>
                <p><strong>Period:</strong> {formatPeriod(selectedDate)}</p>
                <p><strong>Amount:</strong> {total()}</p>
                <p><strong>Status:</strong> {payStatus}</p>
              </div>
              <div className={styles.uploadContainer}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select Date"
                    className={styles.dateInput}
                    value={selectedDateCustom}
                    onChange={(value) => setSelectedDateCustom(value)}
                    TextField={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <CustomInput
                  className={styles.CustomInput}
                  type="text"
                  placeholder="Enter Amount"
                  value={selectedAmount}
                  onChange={(e) => setAmount(e.target.value)}
                  name="amountDue"
                />
                <CustomSelectInput
                  className={styles.CustomInput}
                  selectOptions={paymentModeOptions}
                  defaultOption="Select Mode of Payment"
                  value={selectedMode}
                  onChange={(e) => setMode(e.target.value)}
                  name="amountDue"
                />
                {error && <p style={{ color: "red", fontSize: "14px", marginTop: "8px" }}>{error}</p>}
                <div className={styles.buttonContainer}>
                  <Button className={styles.BtnClass} text="Back" onClick={handleBack} />
                  <Button className={styles.BtnClass} text="Next" onClick={ handleNext} />
                </div>
              </div>
            </>
            )}
            {currentStep === "comment" && (
            <>
              <div className={styles.selectedInfo}>
                <p><strong>Period:</strong> {formatPeriod(selectedDate)}</p>
                <p><strong>Amount:</strong> {total()}</p>
                <p><strong>Status:</strong> {payStatus}</p>
              </div>
              <div className={styles.uploadContainer}>
              <h1>Comment</h1>
              <CustomInput
                className={styles.CustomInputComment}
                type="text"
                placeholder="Enter your comment"
                name="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              {error && <p style={{ color: "red", fontSize: "14px", marginTop: "8px" }}>{error}</p>}
                <div className={styles.buttonContainer}>
                  <Button className={styles.BtnClass} text="Back" onClick={handleBack} />
                  <Button className={styles.BtnClass} text="Preview" onClick={handleNext} />
                </div>
              </div>
            </>
            )}
            {currentStep === "preview" && (
            <>
              <div className={styles.selectedInfo}>
                <p><strong>Period:</strong> {formatPeriod(selectedDate)}</p>
                <p><strong>Amount:</strong> {total()}</p>
                <p><strong>Status:</strong> {payStatus}</p>
              </div>
              <div className={styles.uploadContainer}>
                <h1>Preview</h1>
                <h3>Uploaded File:</h3> 
                <a href={fileURL} download={uploadedFile.name} target="_blank" rel="noopener noreferrer">
                  <Button className={styles.BtnClass} text={"Uploaded File"} />
                </a>
                <div className={styles.infoPreview}>
                  <Button className={styles.BtnClass} text={"Period : " + formatPeriod(selectedDateCustom)} />
                  <Button className={styles.BtnClass} text={`Amount : ${selectedAmount ?? "N/A"}`} />
                  <Button className={styles.BtnClass} text={"Mode : " + (selectedMode ?? "N/A")} />
                </div>
                <Button className={styles.BtnClass} text={comment ? comment: "No comment provided"} />              
                <div className={styles.buttonContainer}>
                  <Button className={styles.BtnClass} text="Back" onClick={ handleBack} />
                  <Button 
                    className={styles.BtnClass}
                    text="Pay"
                    btnType={"primary"}
                    onClick={handleBillPayment}
                  />
                </div>
              </div>
            </>
          )} 
        </div>
      </div>
    </>
  );
};

export default ViewUserPage;
